// src/components/layout/MainLayout.jsx

import React, { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth, db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../context/SubscriptionContext';
import { useTheme } from '../../context/ThemeContext';
import { useActivityTracker } from '../../hooks/useActivityTracker';
import TrialBanner from '../subscription/TrialBanner';
import MarketplaceMenu from '../MarketplaceMenu';
import { collection, query, limit, getDocs, onSnapshot } from 'firebase/firestore';

// --- ICONS ---
// --- ICONS ---
const DashboardIcon = () => <span role="img" aria-label="dashboard">📊</span>;
const OrdersIcon = () => <span role="img" aria-label="orders">📋</span>;
const CustomersIcon = () => <span role="img" aria-label="customers">👥</span>;
const ProductsIcon = () => <span role="img" aria-label="products">📦</span>;
const InvoiceIcon = () => <span role="img" aria-label="invoices">🧾</span>;
const PaymentIcon = () => <span role="img" aria-label="payments">💰</span>;
const SupplierIcon = () => <span role="img" aria-label="suppliers">🏢</span>;
const PurchaseIcon = () => <span role="img" aria-label="purchases">🛒</span>;
const ReportIcon = () => <span role="img" aria-label="reports">📈</span>;
const SettingsIcon = () => <span role="img" aria-label="settings">⚙️</span>;
const TodoIcon = () => <span role="img" aria-label="todo">📝</span>;
const ProductionIcon = () => <span role="img" aria-label="production">🏭</span>; // Added Production Icon
const VideoIcon = () => <span role="img" aria-label="video">🎬</span>;

// --- STYLES ---
const styles = {
  container: { display: 'flex', height: '100vh', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', background: 'var(--bg-secondary)', overflow: 'hidden' },
  sidebar: {
    width: '240px',
    minWidth: '240px',
    background: 'var(--sidebar-bg)',
    color: 'var(--text-primary)',
    display: 'flex',
    flexDirection: 'column',
    borderRight: '1px solid var(--border-color)',
    position: 'relative',
    height: '100vh',
    overflow: 'hidden',
    transition: 'transform 0.3s ease-in-out',
    zIndex: 1000
  },
  // Mobile sidebar overlay
  sidebarMobile: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '280px',
    height: '100vh',
    transform: 'translateX(-100%)',
    boxShadow: 'none'
  },
  sidebarMobileOpen: {
    transform: 'translateX(0)',
    boxShadow: '4px 0 20px rgba(0,0,0,0.15)'
  },
  // Mobile overlay background
  mobileOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'var(--overlay-bg)',
    zIndex: 999,
    opacity: 0,
    visibility: 'hidden',
    transition: 'opacity 0.3s, visibility 0.3s'
  },
  mobileOverlayOpen: {
    opacity: 1,
    visibility: 'visible'
  },
  // Hamburger menu button
  hamburgerButton: {
    display: 'none',
    padding: '10px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: '24px',
    color: '#374151',
    borderRadius: '8px',
    transition: 'background 0.2s'
  },
  hamburgerButtonVisible: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  sidebarHeader: {
    padding: '16px 20px',
    borderBottom: '1px solid var(--border-color)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60px',
    maxHeight: '60px',
    overflow: 'hidden'
  },
  sidebarTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: 'var(--text-primary)',
    margin: 0
  },
  sidebarNav: {
    flex: 1,
    padding: '24px 0',
    overflowY: 'auto',
    overflowX: 'hidden'
  },
  navSection: {
    marginBottom: '32px'
  },
  navSectionTitle: {
    padding: '0 20px 12px',
    fontSize: '11px',
    fontWeight: '600',
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  navLink: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 20px',
    color: 'var(--text-secondary)',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.2s ease',
    borderRadius: '0'
  },
  navLinkActive: {
    color: 'var(--text-accent)',
    backgroundColor: 'var(--bg-hover)',
    fontWeight: '600'
  },
  navLinkHover: {
    backgroundColor: 'var(--bg-secondary)',
    color: 'var(--text-primary)'
  },
  mainContent: {
    flex: 1,
    background: 'var(--bg-secondary)',
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    overflow: 'hidden'
  },
  topBar: {
    background: 'var(--topbar-bg)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    padding: '16px 32px',
    borderBottom: '1px solid var(--border-color)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    minHeight: '72px',
    maxHeight: '72px',
    flexShrink: 0,
    zIndex: 10
  },
  searchContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center'
  },
  searchInput: {
    padding: '8px 12px 8px 36px',
    border: '1px solid var(--border-color)',
    borderRadius: '8px',
    fontSize: '14px',
    width: '400px',
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    background: 'var(--input-bg)',
    color: 'var(--text-primary)'
  },
  searchIcon: {
    position: 'absolute',
    left: '12px',
    color: 'var(--text-muted)',
    fontSize: '16px'
  },
  topBarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  notificationBell: {
    padding: '8px',
    borderRadius: '8px',
    cursor: 'pointer',
    position: 'relative'
  },
  userAvatar: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #06C167, #048C4A)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: '14px',
    fontWeight: '600'
  },
  logoutButton: {
    padding: '8px 16px',
    border: '1px solid var(--border-color)',
    background: 'var(--bg-primary)',
    color: 'var(--text-primary)',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: 'pointer'
  },
  contentArea: {
    flex: 1,
    padding: '32px 40px',
    overflowY: 'auto',
    overflowX: 'hidden',
    height: 'calc(100vh - 72px - 60px)' // Subtracting topBar and TrialBanner heights
  },
  trialBannerContainer: {
    flexShrink: 0,
    maxHeight: '60px'
  }
};

function MainLayout() {
  const { currentUser, userData } = useAuth();
  const { getCurrentPlan, isActive } = useSubscription();
  const { isDarkMode, toggleTheme } = useTheme();
  const activityTracker = useActivityTracker(); // Add activity tracking
  const navigate = useNavigate();
  const location = useLocation();
  const [showLogs, setShowLogs] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchInputRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const [criticalStocks, setCriticalStocks] = useState([]);
  const [showCriticalDropdown, setShowCriticalDropdown] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);

  // Sidebar states
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({
    'ÜRETİM': true // Üretim menüsü varsayılan olarak kapalı olsun (isteğe bağlı)
  });

  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setIsMobileMenuOpen(false);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Ctrl+K / Cmd+K klavye kısayolu
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      // Escape ile arama sonuçlarını kapat
      if (e.key === 'Escape') {
        setShowSearchResults(false);
        setSelectedIndex(-1);
        searchInputRef.current?.blur();
      }
      // Klavye navigasyonu
      if (showSearchResults && searchResults.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSelectedIndex(prev => (prev < searchResults.length - 1 ? prev + 1 : prev));
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSelectedIndex(prev => (prev > 0 ? prev - 1 : prev));
        } else if (e.key === 'Enter' && selectedIndex >= 0) {
          e.preventDefault();
          handleSearchResultClick(searchResults[selectedIndex]);
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showSearchResults, searchResults, selectedIndex]);

  // Kritik Stok Takibi
  useEffect(() => {
    if (!userData?.companyId) return;
    
    const q = query(collection(db, 'companies', userData.companyId, 'products'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const products = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const critical = products.filter(p => {
        const stock = Number(p.quantity || 0);
        const level = Number(p.criticalLevel || 0);
        return stock <= level && level > 0;
      });
      setCriticalStocks(critical);
    });

    return () => unsubscribe();
  }, [userData?.companyId]);

  // Close mobile menu on navigation
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Çıkış yaparken hata oluştu', error);
    }
  };

  const permissions = userData?.permissions || {};
  if (userData?.role === 'admin') {
    Object.assign(permissions, {
      dashboard: true, kasa: true, musteriler: true, stok: true,
      siparisler: true, ayarlar: true, tedarikciler: true,
      alisFaturalari: true, faturalar: true, raporlar: true,
      istekSiparisler: true, stokGirisi: true
    });
  }

  const toggleSidebarCollapse = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const toggleSection = (sectionName) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  const getLinkStyle = (path) => {
    const isActive = location.pathname === path;
    return {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '10px 16px',
      margin: '4px 12px',
      textDecoration: 'none',
      color: isActive ? 'var(--text-accent)' : 'var(--text-secondary)',
      backgroundColor: isActive ? 'var(--bg-hover)' : 'transparent',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: isActive ? '600' : '500',
      transition: 'all 0.2s ease',
      cursor: 'pointer',
      justifyContent: isSidebarCollapsed ? 'center' : 'flex-start',
      whiteSpace: 'nowrap',
      borderLeft: isActive && !isSidebarCollapsed ? '3px solid var(--text-accent)' : '3px solid transparent'
    };
  };

  const getUserInitials = (email) => {
    return email ? email.charAt(0).toUpperCase() : 'U';
  };

  const performGlobalSearch = async (searchQuery) => {
    if (!searchQuery.trim() || !userData?.companyId) {
      setSearchResults([]);
      setShowSearchResults(false);
      setSelectedIndex(-1);
      return;
    }

    setIsSearching(true);
    setSelectedIndex(-1);
    const results = [];
    const companyId = userData.companyId;
    const searchLower = searchQuery.toLowerCase();

    try {
      // Search in customers
      if (permissions.musteriler) {
        const customersQuery = query(collection(db, 'companies', companyId, 'customers'), limit(10));
        const customersSnapshot = await getDocs(customersQuery);
        customersSnapshot.forEach(doc => {
          const data = doc.data();
          if (data.name?.toLowerCase().includes(searchLower) ||
            data.email?.toLowerCase().includes(searchLower) ||
            data.phone?.includes(searchQuery)) {
            results.push({
              id: doc.id,
              type: 'customer',
              title: data.name || 'İsimsiz Müşteri',
              subtitle: data.email || data.phone || '',
              path: '/musteriler',
              icon: '👥'
            });
          }
        });
      }

      // Search in products
      if (permissions.stok) {
        const productsQuery = query(collection(db, 'companies', companyId, 'products'), limit(10));
        const productsSnapshot = await getDocs(productsQuery);
        productsSnapshot.forEach(doc => {
          const data = doc.data();
          if (data.name?.toLowerCase().includes(searchLower) ||
            data.code?.toLowerCase().includes(searchLower) ||
            data.barcode?.includes(searchQuery)) {
            results.push({
              id: doc.id,
              type: 'product',
              title: data.name || 'İsimsiz Ürün',
              subtitle: `Kod: ${data.code || 'Yok'} - Stok: ${data.stock || 0}`,
              path: '/stok',
              icon: '📦'
            });
          }
        });
      }

      // Search in suppliers
      if (permissions.tedarikciler) {
        const suppliersQuery = query(collection(db, 'companies', companyId, 'suppliers'), limit(10));
        const suppliersSnapshot = await getDocs(suppliersQuery);
        suppliersSnapshot.forEach(doc => {
          const data = doc.data();
          if (data.name?.toLowerCase().includes(searchLower) ||
            data.email?.toLowerCase().includes(searchLower) ||
            data.phone?.includes(searchQuery)) {
            results.push({
              id: doc.id,
              type: 'supplier',
              title: data.name || 'İsimsiz Tedarikçi',
              subtitle: data.email || data.phone || '',
              path: '/tedarikciler',
              icon: '🏢'
            });
          }
        });
      }

      // Search in orders
      if (permissions.siparisler) {
        const ordersQuery = query(collection(db, 'companies', companyId, 'orders'), limit(10));
        const ordersSnapshot = await getDocs(ordersQuery);
        ordersSnapshot.forEach(doc => {
          const data = doc.data();
          if (data.orderNumber?.toLowerCase().includes(searchLower) ||
            data.customerName?.toLowerCase().includes(searchLower)) {
            results.push({
              id: doc.id,
              type: 'order',
              title: `Sipariş #${data.orderNumber || doc.id.slice(-6)}`,
              subtitle: `Müşteri: ${data.customerName || 'Bilinmeyen'} - ${data.totalAmount || 0}₺`,
              path: '/siparisler',
              icon: '📋'
            });
          }
        });
      }

      // Search in invoices
      if (permissions.faturalar) {
        const invoicesQuery = query(collection(db, 'companies', companyId, 'invoices'), limit(20));
        const invoicesSnapshot = await getDocs(invoicesQuery);
        invoicesSnapshot.forEach(doc => {
          const data = doc.data();
          if (data.customerName?.toLowerCase().includes(searchLower)) {
            results.push({
              id: doc.id,
              type: 'invoice',
              title: `Fatura #${doc.id.slice(-6)}`,
              subtitle: `Müşteri: ${data.customerName || 'Bilinmeyen'} - ${data.total || 0}₺`,
              path: '/faturalar',
              icon: '🧾'
            });
          }
        });
      }

      // Search in todos
      const todosQuery = query(collection(db, 'companies', companyId, 'todos'), limit(10));
      const todosSnapshot = await getDocs(todosQuery);
      todosSnapshot.forEach(doc => {
        const data = doc.data();
        if (data.title?.toLowerCase().includes(searchLower) ||
          data.description?.toLowerCase().includes(searchLower)) {
          results.push({
            id: doc.id,
            type: 'todo',
            title: data.title || 'İsimsiz Görev',
            subtitle: `Oluşturan: ${data.createdBy || 'Bilinmeyen'}${data.completed ? ' - Tamamlandı' : ''}`,
            path: '/yapilacaklar',
            icon: '📝'
          });
        }
      });

      setSearchResults(results.slice(0, 20)); // Limit to 20 results
      setShowSearchResults(results.length > 0);

      // Track search activity
      if (activityTracker.isTracking && searchQuery.trim()) {
        activityTracker.trackSearch(searchQuery, 'global_search', results.length);
      }
    } catch (error) {
      console.error('Arama sırasında hata:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Debounce search — useRef ile doğru çalışan debounce
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      performGlobalSearch(value);
    }, 300);
  };

  const handleSearchResultClick = (result) => {
    setSearchTerm('');
    setShowSearchResults(false);
    setSelectedIndex(-1);
    navigate(result.path);
  };

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.search-container')) {
        setShowSearchResults(false);
      }
      if (!event.target.closest('.notification-bell')) {
        setShowCriticalDropdown(false);
      }
      if (!event.target.closest('.quick-add-menu')) {
        setShowQuickAdd(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!userData) {
    return (
      <div style={styles.container}>
        <nav style={styles.sidebar}>
          <p style={{ padding: '20px' }}>Yetkiler yükleniyor...</p>
        </nav>
        <main style={styles.mainContent}>
          <p>Yükleniyor...</p>
        </main>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Mobile overlay */}
      {isMobile && (
        <div
          style={{
            ...styles.mobileOverlay,
            ...(isMobileMenuOpen ? styles.mobileOverlayOpen : {})
          }}
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <nav style={{
        ...styles.sidebar,
        ...(isMobile ? styles.sidebarMobile : {}),
        ...(isMobile && isMobileMenuOpen ? styles.sidebarMobileOpen : {}),
        ...(isMobile && !isMobileMenuOpen ? { display: 'flex' } : {}),
        // Desktop styles overrides
        ...(!isMobile && {
          width: isSidebarCollapsed ? '70px' : '240px',
          minWidth: isSidebarCollapsed ? '70px' : '240px',
          display: 'flex'
        })
      }}>
        <div style={{ ...styles.sidebarHeader, padding: isSidebarCollapsed ? '16px 0' : '16px 20px' }}>
          {!isSidebarCollapsed && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              width: '100%'
            }}>
              <div style={{
                fontSize: '16px',
                fontWeight: '600',
                color: 'var(--text-primary)',
                marginBottom: '8px',
                maxWidth: '180px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {currentUser?.email?.split('@')[0] || 'Kullanıcı'}
              </div>
              <div style={{
                fontSize: '12px',
                color: 'var(--text-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontWeight: '500'
              }}>
                {userData?.role === 'admin' ? 'YÖNETİCİ' : userData?.role === 'store' ? 'MAĞAZA' : 'PERSONEL'}
              </div>
            </div>
          )}
          {isSidebarCollapsed && (
            <div style={{ fontWeight: 'bold', color: '#06C167', fontSize: '18px' }}>{getUserInitials(currentUser?.email)}</div>
          )}
        </div>

        <div style={styles.sidebarNav}>
          {userData?.role === 'customer' ? (
            <div style={styles.navSection}>
              {!isSidebarCollapsed && <div style={styles.navSectionTitle}>MÜŞTERİ PORTALI</div>}
              <Link to="/portal" style={getLinkStyle('/portal')} title="Portal Anasayfa">
                <DashboardIcon /> {!isSidebarCollapsed && 'Portal Anasayfa'}
              </Link>
            </div>
          ) : userData?.role === 'store' ? (
            <div style={styles.navSection}>
              {!isSidebarCollapsed && <div style={styles.navSectionTitle}>MAĞAZA PANELİ</div>}
              <Link to="/magaza" style={getLinkStyle('/magaza')} title="Dashboard">
                <DashboardIcon /> {!isSidebarCollapsed && 'Dashboard'}
              </Link>
              <Link to="/magaza/stok" style={getLinkStyle('/magaza/stok')} title="Stok ve Envanter">
                <ProductsIcon /> {!isSidebarCollapsed && 'Stok ve Envanter'}
              </Link>
              <Link to="/magaza/satis" style={getLinkStyle('/magaza/satis')} title="Perakende Satış">
                <PaymentIcon /> {!isSidebarCollapsed && 'Perakende Satış'}
              </Link>
              <Link to="/magaza/satislar" style={getLinkStyle('/magaza/satislar')} title="Satış Geçmişi">
                <ReportIcon /> {!isSidebarCollapsed && 'Satış Geçmişi'}
              </Link>
            </div>
          ) : (
            <>
              {/* ANA MENÜ */}
              <div style={styles.navSection}>
                {!isSidebarCollapsed && (
                  <div
                    style={{ ...styles.navSectionTitle, display: 'flex', justifyContent: 'space-between', cursor: 'pointer' }}
                    onClick={() => toggleSection('ANA MENÜ')}
                  >
                    ANA MENÜ <span>{collapsedSections['ANA MENÜ'] ? '▼' : '▲'}</span>
                  </div>
                )}

                {(isSidebarCollapsed || !collapsedSections['ANA MENÜ']) && (
                  <>
                    {permissions.dashboard && (
                      <Link to="/dashboard" style={getLinkStyle('/dashboard')} title="Dashboard">
                        <DashboardIcon /> {!isSidebarCollapsed && 'Dashboard'}
                      </Link>
                    )}
                    <Link to="/lojistik" style={getLinkStyle('/lojistik')} title="Şoför Hareketleri">
                      <span role="img" aria-label="logistics">🚚</span> {!isSidebarCollapsed && 'Şoför Hareketleri'}
                    </Link>
                    {permissions.siparisler && (
                      <Link to="/siparisler" style={getLinkStyle('/siparisler')} title="Siparişler">
                        <OrdersIcon /> {!isSidebarCollapsed && 'Siparişler'}
                      </Link>
                    )}
                    {permissions.siparisler && (
                      <Link to="/siparisler/tum-sevkiyatlar" style={getLinkStyle('/siparisler/tum-sevkiyatlar')} title="Tüm Sevk İrsaliyeleri">
                        <span style={{ marginLeft: isSidebarCollapsed ? 0 : '20px' }} role="img" aria-label="all shipments">🚚</span> {!isSidebarCollapsed && 'Tüm Sevk İrsaliyeleri'}
                      </Link>
                    )}
                    {permissions.siparisler && (
                      <Link to="/siparisler/direkt-sevk-irsaliyesi" style={getLinkStyle('/siparisler/direkt-sevk-irsaliyesi')} title="Direkt Sevk İrsaliyesi">
                        <span style={{ marginLeft: isSidebarCollapsed ? 0 : '20px' }} role="img" aria-label="direct shipment">📝</span> {!isSidebarCollapsed && 'Direkt Sevk İrsaliyesi'}
                      </Link>
                    )}
                    {permissions.siparisler && (
                      <Link to="/siparisler/faturalastirilmamis-sevkiyatlar" style={getLinkStyle('/siparisler/faturalastirilmamis-sevkiyatlar')} title="Faturalaştırılmamış Sevkiyatlar">
                        <span style={{ marginLeft: isSidebarCollapsed ? 0 : '20px' }} role="img" aria-label="unbilled shipments">🧾</span> {!isSidebarCollapsed && 'Faturalaştırılmamış Sevkiyatlar'}
                      </Link>
                    )}
                    {permissions.musteriler && (
                      <Link to="/musteriler" style={getLinkStyle('/musteriler')} title="Müşteriler">
                        <CustomersIcon /> {!isSidebarCollapsed && 'Müşteriler'}
                      </Link>
                    )}
                    {permissions.musteriler && (
                      <Link to="/musteriler/grup-fiyatlandirmalari" style={getLinkStyle('/musteriler/grup-fiyatlandirmalari')} title="Grup Fiyatlandırmaları">
                        <span style={{ marginLeft: isSidebarCollapsed ? 0 : '20px' }} role="img" aria-label="group-pricing">🏷️</span> {!isSidebarCollapsed && 'Grup Fiyatlandırmaları'}
                      </Link>
                    )}
                    {permissions.stok && (
                      <Link to="/stok" style={getLinkStyle('/stok')} title="Ürünler">
                        <ProductsIcon /> {!isSidebarCollapsed && 'Ürünler'}
                      </Link>
                    )}
                    {permissions.stok && (
                      <Link to="/gorsel-hazirlama" style={getLinkStyle('/gorsel-hazirlama')} title="Görsel Hazırlama">
                        <span style={{ marginLeft: isSidebarCollapsed ? 0 : '20px' }} role="img" aria-label="image-prep">🖼️</span> {!isSidebarCollapsed && 'Görsel Hazırlama'}
                      </Link>
                    )}
                    {permissions.stok && (
                      <Link to="/video-hazirlama" style={getLinkStyle('/video-hazirlama')} title="Video Hazırlama">
                        <span style={{ marginLeft: isSidebarCollapsed ? 0 : '20px' }} role="img" aria-label="video-prep">🎬</span> {!isSidebarCollapsed && 'Video Hazırlama'}
                      </Link>
                    )}
                    {permissions.stok && (
                      <Link to="/depolar" style={getLinkStyle('/depolar')} title="Depolar">
                        <span style={{ marginLeft: isSidebarCollapsed ? 0 : '20px' }} role="img" aria-label="warehouses">🏭</span> {!isSidebarCollapsed && 'Depolar'}
                      </Link>
                    )}
                    <Link to="/yapilacaklar" style={getLinkStyle('/yapilacaklar')} title="Yapılacaklar">
                      <TodoIcon /> {!isSidebarCollapsed && 'Yapılacaklar'}
                    </Link>
                  </>
                )}
              </div>

              {/* ÜRETİM MENÜSÜ */}
              <div style={styles.navSection}>
                {!isSidebarCollapsed && (
                  <div
                    style={{ ...styles.navSectionTitle, display: 'flex', justifyContent: 'space-between', cursor: 'pointer' }}
                    onClick={() => toggleSection('ÜRETİM')}
                  >
                    ÜRETİM <span>{collapsedSections['ÜRETİM'] ? '▼' : '▲'}</span>
                  </div>
                )}
                {(isSidebarCollapsed || !collapsedSections['ÜRETİM']) && (
                  <>
                    <Link to="/uretim" style={getLinkStyle('/uretim')} title="Üretim">
                      <ProductionIcon /> {!isSidebarCollapsed && 'Üretim Paneli'}
                    </Link>
                    <Link to="/uretim/reçeteler" style={getLinkStyle('/uretim/reçeteler')} title="Reçeteler">
                      <span style={{ marginLeft: isSidebarCollapsed ? 0 : '20px' }} role="img" aria-label="recipes">📋</span> {!isSidebarCollapsed && 'Reçeteler'}
                    </Link>
                    <Link to="/uretim/emirler" style={getLinkStyle('/uretim/emirler')} title="Üretim Emirleri">
                      <span style={{ marginLeft: isSidebarCollapsed ? 0 : '20px' }} role="img" aria-label="production orders">📋</span> {!isSidebarCollapsed && 'Üretim Emirleri'}
                    </Link>
                    <Link to="/uretim/fason-tedarikciler" style={getLinkStyle('/uretim/fason-tedarikciler')} title="Fason Tedarikçiler">
                      <span style={{ marginLeft: isSidebarCollapsed ? 0 : '20px' }} role="img" aria-label="subcontractors">👥</span> {!isSidebarCollapsed && 'Fason Tedarikçiler'}
                    </Link>
                    <Link to="/uretim/maliyet-hesaplama" style={getLinkStyle('/uretim/maliyet-hesaplama')} title="Maliyet Hesaplama">
                      <span style={{ marginLeft: isSidebarCollapsed ? 0 : '20px' }} role="img" aria-label="cost calculation">💰</span> {!isSidebarCollapsed && 'Maliyet Hesaplama'}
                    </Link>
                    <Link to="/uretim/karlilik-analizi" style={getLinkStyle('/uretim/karlilik-analizi')} title="Karlılık Analizi">
                      <span style={{ marginLeft: isSidebarCollapsed ? 0 : '20px' }} role="img" aria-label="profitability analysis">📊</span> {!isSidebarCollapsed && 'Karlılık Analizi'}
                    </Link>
                    <Link to="/uretim/is-akisi" style={getLinkStyle('/uretim/is-akisi')} title="İş Akışı Takibi">
                      <span style={{ marginLeft: isSidebarCollapsed ? 0 : '20px' }} role="img" aria-label="workflow">🔄</span> {!isSidebarCollapsed && 'İş Akışı Takibi'}
                    </Link>
                    <Link to="/uretim/malzeme-siparisleri" style={getLinkStyle('/uretim/malzeme-siparisleri')} title="Malzeme Siparişleri">
                      <span style={{ marginLeft: isSidebarCollapsed ? 0 : '20px' }} role="img" aria-label="material orders">📦</span> {!isSidebarCollapsed && 'Malzeme Siparişleri'}
                    </Link>
                    <Link to="/uretim/aksesuar-yonetimi" style={getLinkStyle('/uretim/aksesuar-yonetimi')} title="Aksesuar Yönetimi">
                      <span style={{ marginLeft: isSidebarCollapsed ? 0 : '20px' }} role="img" aria-label="accessory management">💎</span> {!isSidebarCollapsed && 'Aksesuar Yönetimi'}
                    </Link>
                    <Link to="/uretim/raporlar" style={getLinkStyle('/uretim/raporlar')} title="Üretim Raporlar">
                      <span style={{ marginLeft: isSidebarCollapsed ? 0 : '20px' }} role="img" aria-label="production reports">📈</span> {!isSidebarCollapsed && 'Raporlar'}
                    </Link>
                  </>
                )}
              </div>

              {/* SATIŞ */}
              <div style={styles.navSection}>
                {!isSidebarCollapsed && (
                  <div
                    style={{ ...styles.navSectionTitle, display: 'flex', justifyContent: 'space-between', cursor: 'pointer' }}
                    onClick={() => toggleSection('SATIŞ')}
                  >
                    SATIŞ <span>{collapsedSections['SATIŞ'] ? '▼' : '▲'}</span>
                  </div>
                )}
                {(isSidebarCollapsed || !collapsedSections['SATIŞ']) && (
                  <>
                    {permissions.faturalar && (
                      <Link to="/faturalar/satis" style={getLinkStyle('/faturalar/satis')} title="Satış Faturaları">
                        <InvoiceIcon /> {!isSidebarCollapsed && 'Satış Faturaları'}
                      </Link>
                    )}
                    {permissions.siparisler && (
                      <Link to="/fiyat-teklifleri" style={getLinkStyle('/fiyat-teklifleri')} title="Fiyat Teklifleri">
                        <span role="img" aria-label="price-quotes">📋</span> {!isSidebarCollapsed && 'Fiyat Teklifleri'}
                      </Link>
                    )}
                    {permissions.kasa && (
                      <Link to="/kasa" style={getLinkStyle('/kasa')} title="Kasa Yönetimi">
                        <PaymentIcon /> {!isSidebarCollapsed && 'Kasa Yönetimi'}
                      </Link>
                    )}
                    {permissions.siparisler && (
                      <Link to="/iadeler" style={getLinkStyle('/iadeler')} title="Ürün İadeleri">
                        <span role="img" aria-label="returns">↩️</span> {!isSidebarCollapsed && 'Ürün İadeleri'}
                      </Link>
                    )}
                  </>
                )}
              </div>

              {/* PAZARYERİ FİYATLAMA */}
              <div style={styles.navSection}>
                {!isSidebarCollapsed && (
                  <div
                    style={{ ...styles.navSectionTitle, display: 'flex', justifyContent: 'space-between', cursor: 'pointer' }}
                    onClick={() => toggleSection('PAZARYERİ FİYATLAMA')}
                  >
                    PAZARYERİ FİYATLAMA <span>{collapsedSections['PAZARYERİ FİYATLAMA'] ? '▼' : '▲'}</span>
                  </div>
                )}
                {(isSidebarCollapsed || !collapsedSections['PAZARYERİ FİYATLAMA']) && (
                  <>
                    <Link to="/pazaryeri-fiyatlama" style={getLinkStyle('/pazaryeri-fiyatlama')} title="Pazar Fiyatlama">
                      <span role="img" aria-label="pricing">💰</span> {!isSidebarCollapsed && 'Pazar Fiyatlama'}
                    </Link>
                    <Link to="/pazaryeri-fiyatlama/toplu" style={getLinkStyle('/pazaryeri-fiyatlama/toplu')} title="Toplu Fiyatlama">
                      <span style={{ marginLeft: isSidebarCollapsed ? 0 : '20px' }} role="img" aria-label="bulk-pricing">📑</span> {!isSidebarCollapsed && 'Toplu Fiyatlama'}
                    </Link>
                  </>
                )}
              </div>

              {/* ALIŞ */}
              <div style={styles.navSection}>
                {!isSidebarCollapsed && (
                  <div
                    style={{ ...styles.navSectionTitle, display: 'flex', justifyContent: 'space-between', cursor: 'pointer' }}
                    onClick={() => toggleSection('ALIŞ')}
                  >
                    ALIŞ <span>{collapsedSections['ALIŞ'] ? '▼' : '▲'}</span>
                  </div>
                )}
                {(isSidebarCollapsed || !collapsedSections['ALIŞ']) && (
                  <>
                    {permissions.tedarikciler && (
                      <Link to="/tedarikciler" style={getLinkStyle('/tedarikciler')} title="Tedarikçiler">
                        <SupplierIcon /> {!isSidebarCollapsed && 'Tedarikçiler'}
                      </Link>
                    )}
                    {permissions.alisFaturalari && (
                      <Link to="/alis-faturalari" style={getLinkStyle('/alis-faturalari')} title="Alış Faturaları">
                        <PurchaseIcon /> {!isSidebarCollapsed && 'Alış Faturaları'}
                      </Link>
                    )}
                    {permissions.tedarikciler && (
                      <Link to="/hammadde-satin-alma" style={getLinkStyle('/hammadde-satin-alma')} title="Hammadde Satın Alma">
                        <span role="img" aria-label="raw-materials">📦</span> {!isSidebarCollapsed && 'Hammadde Satın Alma'}
                      </Link>
                    )}
                  </>
                )}
              </div>

              {/* RAPORLAR */}
              <div style={styles.navSection}>
                {!isSidebarCollapsed && (
                  <div
                    style={{ ...styles.navSectionTitle, display: 'flex', justifyContent: 'space-between', cursor: 'pointer' }}
                    onClick={() => toggleSection('RAPORLAR')}
                  >
                    RAPORLAR <span>{collapsedSections['RAPORLAR'] ? '▼' : '▲'}</span>
                  </div>
                )}
                {(isSidebarCollapsed || !collapsedSections['RAPORLAR']) && (
                  <>
                    {permissions.raporlar && (
                      <Link to="/raporlar" style={getLinkStyle('/raporlar')} title="Raporlar">
                        <ReportIcon /> {!isSidebarCollapsed && 'Raporlar'}
                      </Link>
                    )}
                    {permissions.raporlar && (
                      <Link to="/raporlar/yaslandirma" style={getLinkStyle('/raporlar/yaslandirma')} title="Yaşlandırma Raporu">
                        <span style={{ marginLeft: isSidebarCollapsed ? 0 : '20px' }} role="img" aria-label="aging">⏳</span> {!isSidebarCollapsed && 'Yaşlandırma Raporu'}
                      </Link>
                    )}
                  </>
                )}
              </div>

              {/* AYARLAR */}
              <div style={styles.navSection}>
                {!isSidebarCollapsed && (
                  <div
                    style={{ ...styles.navSectionTitle, display: 'flex', justifyContent: 'space-between', cursor: 'pointer' }}
                    onClick={() => toggleSection('AYARLAR')}
                  >
                    AYARLAR <span>{collapsedSections['AYARLAR'] ? '▼' : '▲'}</span>
                  </div>
                )}
                {(isSidebarCollapsed || !collapsedSections['AYARLAR']) && (
                  <>
                    <Link to="/personnel-menu" style={getLinkStyle('/personnel-menu')} title="Personel Menü">
                      <span role="img" aria-label="personnel">👤</span> {!isSidebarCollapsed && 'Personel Menü'}
                    </Link>
                    <Link to="/abonelik" style={getLinkStyle('/abonelik')} title="Abonelik">
                      <span role="img" aria-label="subscription">👑</span> {!isSidebarCollapsed && 'Abonelik'}
                    </Link>
                    {permissions.ayarlar && (
                      <Link to="/kullanici-aktiviteleri" style={getLinkStyle('/kullanici-aktiviteleri')} title="Kullanıcı Aktiviteleri">
                        <span role="img" aria-label="activities">📊</span> {!isSidebarCollapsed && 'Kullanıcı Aktiviteleri'}
                      </Link>
                    )}
                    {permissions.ayarlar && userData?.role === 'admin' && (
                      <Link to="/ayarlar" style={getLinkStyle('/ayarlar')} title="Ayarlar">
                        <SettingsIcon /> {!isSidebarCollapsed && 'Ayarlar'}
                      </Link>
                    )}
                    <MarketplaceMenu isCollapsed={isSidebarCollapsed} />
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </nav>

      <main style={styles.mainContent}>
        <div style={{ ...styles.topBar, padding: isMobile ? '12px 16px' : '16px 32px' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {/* Collapse/Expand Sidebar Button (Desktop) */}
            {!isMobile && (
              <button
                onClick={toggleSidebarCollapse}
                style={{
                  marginRight: '16px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '20px',
                  color: 'var(--text-secondary)',
                  padding: '4px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.2s',
                  height: '32px',
                  width: '32px'
                }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                title={isSidebarCollapsed ? "Menüyü Genişlet" : "Menüyü Daralt"}
              >
                {isSidebarCollapsed ? '»' : '«'}
              </button>
            )}

            {/* Hamburger Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              style={{
                ...styles.hamburgerButton,
                ...(isMobile ? styles.hamburgerButtonVisible : {})
              }}
            >
              <span role="img" aria-label="menu">☰</span>
            </button>

            {/* Dinamik Sayfa Başlığı / Breadcrumb */}
            {!isMobile && (
              <div style={{ 
                fontSize: '18px', 
                fontWeight: '600', 
                color: 'var(--text-primary)', 
                textTransform: 'capitalize',
                marginLeft: '8px',
                letterSpacing: '-0.02em'
              }}>
                {location.pathname === '/' || location.pathname === '/dashboard' ? 'Dashboard' : location.pathname.split('/').filter(Boolean).pop().replace(/-/g, ' ')}
              </div>
            )}
          </div>

          <div style={{ ...styles.searchContainer, position: 'relative', flex: isMobile ? 1 : 'none', marginLeft: 'auto', marginRight: '20px' }} className="search-container">
            <span style={styles.searchIcon} role="img" aria-label="search">🔍</span>
            <input
              ref={searchInputRef}
              type="text"
              placeholder={isMobile ? "Ara..." : "Müşteri, ürün, sipariş ara... (Ctrl+K)"}
              value={searchTerm}
              onChange={handleSearchChange}
              onFocus={() => searchResults.length > 0 && setShowSearchResults(true)}
              style={{ ...styles.searchInput, width: isMobile ? '100%' : '400px' }}
            />
            {isSearching && (
              <div style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#6b7280',
                fontSize: '12px'
              }}>
                Aranıyor...
              </div>
            )}

            {/* Search Results Dropdown */}
            {showSearchResults && searchResults.length > 0 && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                border: '1px solid var(--border-color)',
                borderRadius: '12px',
                boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
                maxHeight: '450px',
                overflowY: 'auto',
                zIndex: 1000,
                marginTop: '8px',
                padding: '8px'
              }}>
                <div style={{
                  padding: '8px 12px 12px',
                  fontSize: '11px',
                  fontWeight: '700',
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Sonuçlar ({searchResults.length})
                </div>
                {searchResults.map((result, index) => (
                  <div
                    key={`${result.type}-${result.id}`}
                    onClick={() => handleSearchResultClick(result)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    style={{
                      padding: '10px 12px',
                      cursor: 'pointer',
                      borderRadius: '8px',
                      backgroundColor: selectedIndex === index ? 'var(--bg-hover)' : 'transparent',
                      transition: 'all 0.15s ease',
                      marginBottom: '2px'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontSize: '16px' }}>{result.icon}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '500',
                          color: 'var(--text-primary)',
                          marginBottom: '2px'
                        }}>
                          {result.title}
                        </div>
                        <div style={{
                          fontSize: '12px',
                          color: 'var(--text-secondary)'
                        }}>
                          {result.subtitle}
                        </div>
                      </div>
                      <div style={{
                        fontSize: '10px',
                        color: '#9ca3af',
                        textTransform: 'uppercase',
                        fontWeight: '600',
                        letterSpacing: '0.5px'
                      }}>
                        {result.type === 'customer' ? 'MÜŞTERİ' :
                          result.type === 'product' ? 'ÜRÜN' :
                            result.type === 'supplier' ? 'TEDARİKÇİ' :
                              result.type === 'order' ? 'SİPARİŞ' : 'GÖREV'}
                      </div>
                    </div>
                  </div>
                ))}
                {searchTerm && searchResults.length === 0 && !isSearching && (
                  <div style={{
                    padding: '20px',
                    textAlign: 'center',
                    color: '#6b7280',
                    fontSize: '14px'
                  }}>
                    "{searchTerm}" için sonuç bulunamadı
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={styles.topBarRight}>
            
            {/* Quick Action (+) Button */}
            {(!isMobile && permissions.stok) && (
              <div style={{ position: 'relative' }} className="quick-add-menu">
                <button
                  onClick={() => setShowQuickAdd(!showQuickAdd)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: 'none',
                    background: 'var(--text-accent)',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    boxShadow: 'var(--shadow-sm)',
                    transition: 'opacity 0.2s'
                  }}
                  onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
                  onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                >
                  <span>+</span> Hızlı Ekle
                </button>
                
                {showQuickAdd && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    width: '200px',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '12px',
                    boxShadow: 'var(--shadow-md)',
                    marginTop: '8px',
                    zIndex: 1001,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    {permissions.stok && (
                      <div onClick={() => { navigate('/stok/yeni'); setShowQuickAdd(false); }} style={{ padding: '12px 16px', cursor: 'pointer', fontSize: '14px', color: 'var(--text-primary)', borderBottom: '1px solid var(--border-color)' }}>
                        📦 Yeni Ürün
                      </div>
                    )}
                    {permissions.siparisler && (
                      <div onClick={() => { navigate('/siparisler/yeni'); setShowQuickAdd(false); }} style={{ padding: '12px 16px', cursor: 'pointer', fontSize: '14px', color: 'var(--text-primary)', borderBottom: '1px solid var(--border-color)' }}>
                        📋 Yeni Sipariş
                      </div>
                    )}
                    {permissions.tedarikciler && (
                      <div onClick={() => { navigate('/hammadde-satin-alma'); setShowQuickAdd(false); }} style={{ padding: '12px 16px', cursor: 'pointer', fontSize: '14px', color: 'var(--text-primary)' }}>
                        🛒 Hammadde Alımı
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Dark Mode Toggle */}
            <button
              onClick={toggleTheme}
              style={{
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid var(--border-color)',
                background: 'var(--bg-secondary)',
                cursor: 'pointer',
                fontSize: '16px',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
              title={isDarkMode ? 'Aydınlık Temaya Geç' : 'Karanlık Temaya Geç'}
            >
              {isDarkMode ? '☀️' : '🌙'}
              {!isMobile && <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '500' }}>{isDarkMode ? 'Açık' : 'Koyu'}</span>}
            </button>

            <div style={{
              padding: '4px 12px',
              borderRadius: '16px',
              backgroundColor: isActive() ? '#dcfce7' : '#fef3c7',
              color: isActive() ? '#06C167' : '#d97706',
              fontSize: '12px',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              {getCurrentPlan().name}
            </div>

            <div
              style={{ ...styles.notificationBell, background: criticalStocks.length > 0 ? 'var(--bg-hover)' : 'transparent' }}
              onClick={() => setShowCriticalDropdown(!showCriticalDropdown)}
              className="notification-bell"
            >
              <span role="img" aria-label="notifications">🔔</span>
              {criticalStocks.length > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '2px',
                  right: '2px',
                  background: '#ef4444',
                  color: 'white',
                  borderRadius: '50%',
                  width: '18px',
                  height: '18px',
                  fontSize: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '700',
                  border: '2px solid var(--topbar-bg)'
                }}>
                  {criticalStocks.length}
                </span>
              )}

              {/* Critical Stock Dropdown */}
              {showCriticalDropdown && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  width: '320px',
                  background: 'rgba(255, 255, 255, 0.98)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '16px',
                  boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
                  marginTop: '12px',
                  zIndex: 1001,
                  overflow: 'hidden'
                }} onClick={e => e.stopPropagation()}>
                  <div style={{
                    padding: '16px',
                    borderBottom: '1px solid var(--border-color)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: '#f8fafc'
                  }}>
                    <span style={{ fontWeight: '700', fontSize: '14px', color: '#1e293b' }}>Kritik Stok Uyarıları</span>
                    <span style={{ 
                      fontSize: '11px', 
                      background: '#fee2e2', 
                      color: '#ef4444', 
                      padding: '2px 8px', 
                      borderRadius: '12px', 
                      fontWeight: '600' 
                    }}>{criticalStocks.length} Ürün</span>
                  </div>
                  <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                    {criticalStocks.length === 0 ? (
                      <div style={{ padding: '32px 16px', textAlign: 'center', color: '#64748b' }}>
                        <div style={{ fontSize: '24px', marginBottom: '8px' }}>✅</div>
                        <div style={{ fontSize: '13px', fontWeight: '500' }}>Kritik seviyede ürün bulunmuyor.</div>
                      </div>
                    ) : (
                      criticalStocks.map(product => (
                        <div 
                          key={product.id}
                          onClick={() => {
                            navigate('/stok');
                            setShowCriticalDropdown(false);
                          }}
                          style={{
                            padding: '12px 16px',
                            borderBottom: '1px solid #f1f5f9',
                            cursor: 'pointer',
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <span style={{ fontWeight: '600', fontSize: '13px', color: '#334155' }}>{product.name}</span>
                            <span style={{ color: '#ef4444', fontWeight: '700', fontSize: '12px' }}>{product.quantity} {product.unit || 'Adet'}</span>
                          </div>
                          <div style={{ fontSize: '11px', color: '#94a3b8', display: 'flex', justifyContent: 'space-between' }}>
                            <span>Kritik Eşik: {product.criticalLevel}</span>
                            <span>{product.groupId ? 'Grup: ' + product.groupId : ''}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <div 
                    onClick={() => { navigate('/stok'); setShowCriticalDropdown(false); }}
                    style={{
                      padding: '12px',
                      textAlign: 'center',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#06C167',
                      cursor: 'pointer',
                      background: '#f1f5f9'
                    }}
                  >
                    Tüm Stokları Görüntüle
                  </div>
                </div>
              )}
            </div>

            <div style={styles.userAvatar}>
              {getUserInitials(currentUser?.email)}
            </div>

            <button
              onClick={handleLogout}
              style={styles.logoutButton}
            >
              Çıkış Yap
            </button>
          </div>
        </div>

        <div style={styles.trialBannerContainer}>
          <TrialBanner />
        </div>

        <div style={{ ...styles.contentArea, padding: isMobile ? '16px' : '32px 40px' }}>
          <Outlet context={{ canViewSensitiveData: permissions.ayarlar || userData?.role === 'admin' }} />
        </div>
      </main>

    </div >
  );
}

export default MainLayout;
